import type { Dispatch, FC, SetStateAction } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import { helpsiteTrackButton } from "@helpers/helpsite-mixpanel";
import type { IHelpsiteArticleDropdownFields } from "@types-contentful";
import Icon from "@components/Icon";
import HelpArticleContent from "./HelpArticleContent";

const iconSizes = {
	iconMobile: 34,
	iconTablet: 34,
	iconLaptop: 34,
	iconDesktop: 34,
	iconDesktopLarge: 34,
};

const DropdownContainer = styled.div`
	border: none;
	border-bottom: 1px solid rgba(0, 0, 0, 0.8);
	margin: 0;
	overflow: hidden;
	/* Offset scroll position so dropdown title stays visible below sticky nav */
	scroll-margin-top: 80px;

	&:last-child {
		border-bottom: none;
	}
`;

const DropdownHeader = styled.button`
	width: 100%;
	padding: 20px;
	background-color: #ffffff !important;
	border: none !important;
	text-align: left;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-size: 18px;
	font-weight: 600;
	color: #1a1a1a;
	transition: none !important;
	box-shadow: none !important;
	outline: none !important;

	&:hover,
	&:active,
	&:focus {
		background-color: #ffffff !important;
		border: none !important;
		box-shadow: none !important;
		transform: none !important;
		transition: none !important;
	}
`;

const DropdownIconWrapper = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	min-width: 44px;
	min-height: 44px;
	cursor: pointer;
	pointer-events: auto;
`;

const DropdownContent = styled.div<{ isOpen: boolean }>`
	max-height: ${(props) => (props.isOpen ? "5000px" : "0")};
	overflow: hidden;
	/* transition: max-height 0.3s ease-out; */
	padding: ${(props) => (props.isOpen ? "20px" : "0 20px")};
`;

interface HelpArticleDropdownProps {
	dropdown: IHelpsiteArticleDropdownFields;
	dropdownId: string;
	isOpen: boolean;
	onToggle: () => void;
	openDropdownSlug?: string | null;
	setOpenDropdownSlug?: Dispatch<SetStateAction<string | null>>;
}

const HelpArticleDropdown: FC<HelpArticleDropdownProps> = ({
	dropdown,
	dropdownId,
	isOpen,
	onToggle,
	openDropdownSlug,
	setOpenDropdownSlug,
}) => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];
	const { title, content } = dropdown;

	const handleToggle = () => {
		helpsiteTrackButton({
			helpsite_section: "article_dropdown",
			page_path: pagePath,
			label: title || "Article dropdown",
			cta_type: isOpen ? "close" : "open",
		});
		onToggle();
	};

	return (
		<DropdownContainer id={dropdownId}>
			<DropdownHeader onClick={handleToggle}>
				<span>{title || "Click to expand"}</span>
				<DropdownIconWrapper
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						handleToggle();
					}}
				>
					<Icon
						iconName={isOpen ? "close" : "add"}
						iconSizes={iconSizes}
						alt=""
					/>
				</DropdownIconWrapper>
			</DropdownHeader>
			{content && content.length > 0 && (
				<DropdownContent isOpen={isOpen}>
					{content.map((contentItem) => (
						<HelpArticleContent
							key={contentItem.sys.id}
							content={contentItem.fields}
							openDropdownSlug={openDropdownSlug}
							setOpenDropdownSlug={setOpenDropdownSlug}
						/>
					))}
				</DropdownContent>
			)}
		</DropdownContainer>
	);
};

export default HelpArticleDropdown;
